import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { WelcomeStep } from '../components/WelcomeStep';
import { RoleStep, type UserRole } from '../components/RoleStep';
import { PersonalInfoStep, type Gender } from '../components/PersonalInfoStep';
import { DueDateStep } from '../components/DueDateStep';
import { PregnancyTypeStep } from '../components/PregnancyTypeStep';
import { ChildNumberStep } from '../components/ChildNumberStep';
import { PartnerStep } from '../components/PartnerStep';
import { PremiumStep } from '../components/PremiumStep';
import { PromoCodeStep, type ValidatedPromoCode } from '../components/PromoCodeStep';
import { CompletionStep } from '../components/CompletionStep';
import { PregnancyDetailsStep } from '../components/PregnancyDetailsStep';
import { useAuth } from '@/features/auth';
import { createPartnerInvite } from '@/features/partner/services/partnerService';
import { usePregnancyStore } from '@/store/pregnancyStore';
import { useSubscription } from '@/features/subscription/hooks/useSubscription';
import { useTranslation, useLocale } from '@parentpath/i18n';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { getUserDisplayName } from '@/lib/userName';

export interface OnboardingData {
  // User role
  userRole: UserRole | null;
  // Personal info (profile)
  birthDate: string;
  gender: Gender | null;
  // Pregnancy info
  expectedDueDate: Date | null;
  childCount: number; // 1 = single, 2 = twins, etc.
  childNumber: number; // 1st, 2nd, 3rd child for the family
  // Baby already born
  babyAlreadyBorn: boolean;
  actualBirthDate: Date | null;
  // Partner
  invitePartner: boolean;
  partnerEmail?: string;
}

interface PregnancyData {
  id: string;
  expected_due_date: string;
  child_count: number;
  child_number: number;
  is_high_risk: boolean;
  baby_names: string[];
  created_by: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  completion_type?: 'birth' | 'ended' | null;
  actual_birth_date?: string | null;
  baby_data?: Record<string, unknown> | null;
}

interface PartnerConnectionData {
  id: string;
  pregnancy_id: string;
  user_1_id: string;
  pregnancy: PregnancyData | null;
  inviterName: string;
}

// Standard onboarding: 9 steps (Welcome, Role, Personal, DueDate, Type, ChildNum, Partner, BetaCode, Completion)
const STANDARD_TOTAL_STEPS = 9;
// Partner onboarding steps vary based on whether inviter has premium:
// - With inviter premium: 5 steps (Welcome, PregnancyDetails, Role, PersonalInfo, Completion)
// - Without inviter premium: 7 steps (adds Premium + PromoCode steps)
const PARTNER_BASE_STEPS = 5;
const PARTNER_WITH_PREMIUM_UPSELL_STEPS = 7;

// Inviter premium status for partner flow
interface InviterPremiumStatus {
  hasPremium: boolean;
  isBetaTester: boolean;
  subscriptionTier: string | null;
}

// Partner's own subscription info (for cancellation warning)
interface PartnerSubscriptionInfo {
  hasActiveSubscription: boolean;
  subscriptionEndDate: string | null;
  stripeSubscriptionId: string | null;
}

export function OnboardingPage() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { setPregnancy } = usePregnancyStore();
  const subscription = useSubscription();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCheckingPartnerStatus, setIsCheckingPartnerStatus] = useState(true);
  const [isInvitedPartner, setIsInvitedPartner] = useState(false);
  const [partnerConnection, setPartnerConnection] = useState<PartnerConnectionData | null>(null);
  const [inviterPremiumStatus, setInviterPremiumStatus] = useState<InviterPremiumStatus | null>(null);
  const [partnerSubscriptionInfo, setPartnerSubscriptionInfo] = useState<PartnerSubscriptionInfo | null>(null);

  // Track if user already has an active partner connection (for standard flow)
  const [existingPartnerConnection, setExistingPartnerConnection] = useState<{
    id: string;
    partnerName: string | null;
    status: string;
  } | null>(null);

  const [data, setData] = useState<OnboardingData>({
    userRole: null,
    birthDate: '',
    gender: null,
    expectedDueDate: null,
    childCount: 1,
    childNumber: 1,
    babyAlreadyBorn: false,
    actualBirthDate: null,
    invitePartner: false,
    partnerEmail: undefined,
  });
  const { t } = useTranslation('onboarding');
  const { locale } = useLocale();

  // Track if profile data was loaded (for potential future auto-skip feature)
  const [, setHasExistingProfileData] = useState(false);

  // Load existing profile data (birthDate & gender) if available
  useEffect(() => {
    async function loadExistingProfileData() {
      if (!user) return;

      try {
        const { data: profile } = await supabase
          .from('profiles')
          .select('birth_date, gender, user_role')
          .eq('id', user.id)
          .single();

        if (profile) {
          // Pre-fill data if it exists
          const updates: Partial<OnboardingData> = {};

          if (profile.birth_date) {
            updates.birthDate = profile.birth_date;
          }
          if (profile.gender) {
            updates.gender = profile.gender as Gender;
          }
          if (profile.user_role) {
            updates.userRole = profile.user_role as UserRole;
          }

          if (Object.keys(updates).length > 0) {
            setData(prev => ({ ...prev, ...updates }));
            // Mark that we have existing data (birthDate and gender)
            if (profile.birth_date && profile.gender) {
              setHasExistingProfileData(true);
            }
          }
        }
      } catch (err) {
        console.error('Error loading existing profile data:', err);
      }
    }

    loadExistingProfileData();
  }, [user]);

  // Premium/Promo state
  const [showPromoCodeStep, setShowPromoCodeStep] = useState(false);
  const [validatedPromoCode, setValidatedPromoCode] = useState<ValidatedPromoCode | null>(null);

  const displayName = getUserDisplayName(user);

  // Check if user is an invited partner
  useEffect(() => {
    async function checkInvitedPartner() {
      if (!user) {
        setIsCheckingPartnerStatus(false);
        return;
      }

      try {
        console.log('[Onboarding] Checking partner status for user:', user.id);

        // Check if user is user_2 in any active partner_connection
        const { data: connection, error: connectionError } = await supabase
          .from('partner_connections')
          .select('id, pregnancy_id, user_1_id')
          .eq('user_2_id', user.id)
          .eq('status', 'active')
          .maybeSingle();

        console.log('[Onboarding] Partner connection result:', { connection, connectionError });

        if (connectionError) {
          console.error('[Onboarding] Error fetching partner connection:', connectionError);
        }

        if (connection && !connectionError) {
          // Fetch the pregnancy details
          const { data: pregnancy, error: pregnancyError } = await supabase
            .from('pregnancies')
            .select('id, expected_due_date, child_count, child_number, is_high_risk, baby_names, created_by, is_active, created_at, updated_at, completion_type, actual_birth_date, baby_data')
            .eq('id', connection.pregnancy_id)
            .single();

          if (pregnancyError) {
            console.error('[Onboarding] Error fetching pregnancy:', pregnancyError);
          }

          // Fetch the inviter's profile with premium status
          const { data: inviterProfile, error: inviterError } = await supabase
            .from('profiles')
            .select('display_name, is_beta_tester, subscription_tier, subscription_status, stripe_subscription_id')
            .eq('id', connection.user_1_id)
            .single();

          if (inviterError) {
            console.error('[Onboarding] Error fetching inviter profile:', inviterError);
          }

          // Fetch the current user's (invited partner's) subscription info
          const { data: partnerProfile, error: partnerError } = await supabase
            .from('profiles')
            .select('subscription_tier, subscription_status, stripe_subscription_id, subscription_current_period_end')
            .eq('id', user.id)
            .single();

          if (partnerError && partnerError.code !== 'PGRST116') {
            console.error('[Onboarding] Error fetching partner profile:', partnerError);
          }

          // Determine inviter's premium status
          const inviterHasPremium = !!(
            inviterProfile?.is_beta_tester ||
            inviterProfile?.subscription_tier === 'premium' ||
            (inviterProfile?.subscription_status === 'active' && inviterProfile?.stripe_subscription_id)
          );

          setInviterPremiumStatus({
            hasPremium: inviterHasPremium,
            isBetaTester: inviterProfile?.is_beta_tester || false,
            subscriptionTier: inviterProfile?.subscription_tier || null,
          });

          // Determine partner's subscription info (for potential cancellation warning)
          const partnerHasActiveSubscription = !!(
            partnerProfile?.subscription_status === 'active' &&
            partnerProfile?.stripe_subscription_id
          );

          setPartnerSubscriptionInfo({
            hasActiveSubscription: partnerHasActiveSubscription,
            subscriptionEndDate: partnerProfile?.subscription_current_period_end || null,
            stripeSubscriptionId: partnerProfile?.stripe_subscription_id || null,
          });

          console.log('[Onboarding] User IS an invited partner:', {
            connection,
            pregnancy,
            inviterProfile,
            inviterHasPremium,
            partnerHasActiveSubscription
          });
          setIsInvitedPartner(true);
          setPartnerConnection({
            id: connection.id,
            pregnancy_id: connection.pregnancy_id,
            user_1_id: connection.user_1_id,
            pregnancy: pregnancy as PregnancyData | null,
            inviterName: inviterProfile?.display_name || t('messages.yourPartner'),
          });
          // Pre-select partner/father role for invited partners
          setData(prev => ({ ...prev, userRole: 'father' }));
        } else {
          console.log('[Onboarding] User is NOT an invited partner - checking for existing connections...');

          // Check if user has created an active partner connection (as user_1)
          const { data: existingConnection, error: existingError } = await supabase
            .from('partner_connections')
            .select('id, status, user_2_id')
            .eq('user_1_id', user.id)
            .eq('status', 'active')
            .maybeSingle();

          if (existingError && existingError.code !== 'PGRST116') {
            console.error('[Onboarding] Error checking existing connection:', existingError);
          }

          if (existingConnection) {
            // Fetch partner's name if connected
            let partnerName: string | null = null;
            if (existingConnection.user_2_id) {
              const { data: partnerProfile } = await supabase
                .from('profiles')
                .select('display_name')
                .eq('id', existingConnection.user_2_id)
                .single();
              partnerName = partnerProfile?.display_name || null;
            }

            console.log('[Onboarding] User has existing partner connection:', existingConnection);
            setExistingPartnerConnection({
              id: existingConnection.id,
              partnerName,
              status: existingConnection.status,
            });
          }
        }
      } catch (err) {
        console.error('[Onboarding] Exception checking partner status:', err);
      } finally {
        setIsCheckingPartnerStatus(false);
      }
    }

    checkInvitedPartner();
  }, [user]);

  // Calculate total steps for partner flow based on inviter's premium status
  const getPartnerTotalSteps = () => {
    if (!inviterPremiumStatus) return PARTNER_BASE_STEPS;
    // If inviter has premium, partner doesn't need premium upsell
    return inviterPremiumStatus.hasPremium ? PARTNER_BASE_STEPS : PARTNER_WITH_PREMIUM_UPSELL_STEPS;
  };

  // Calculate total steps for standard flow
  // Skip Premium step (step 8) if user already has premium
  const getStandardTotalSteps = () => {
    // If user already has premium, skip the premium/promo step
    if (subscription.isPremium) {
      return STANDARD_TOTAL_STEPS - 1; // 8 steps instead of 9
    }
    return STANDARD_TOTAL_STEPS;
  };

  const totalSteps = isInvitedPartner ? getPartnerTotalSteps() : getStandardTotalSteps();

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleUpdateData = (updates: Partial<OnboardingData>) => {
    setData((prev) => ({ ...prev, ...updates }));
  };

  // Get effective gender based on role
  const getEffectiveGender = (): Gender | null => {
    if (data.userRole === 'mother') return 'female';
    if (data.userRole === 'father') return 'male';
    return data.gender; // For partner, use selected gender
  };

  // Calculate if this is a high-risk pregnancy (only for mothers)
  const calculateHighRisk = (): boolean => {
    // High risk if: multiples
    if (data.childCount > 1) return true;

    // High risk age only applies to mothers
    if (data.userRole === 'mother' && data.birthDate) {
      const birth = new Date(data.birthDate);
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--;
      }
      if (age >= 35) return true;
    }

    return false;
  };

  // Complete onboarding for invited partner (simplified flow)
  const handlePartnerComplete = async () => {
    if (!user || !partnerConnection) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const effectiveGender = getEffectiveGender();

      // First, delete any existing pregnancy that the invited user might have created
      const { data: existingPregnancies } = await supabase
        .from('pregnancies')
        .select('id')
        .eq('created_by', user.id)
        .eq('is_active', true);

      if (existingPregnancies && existingPregnancies.length > 0) {
        console.log('Deleting existing pregnancies for invited partner:', existingPregnancies);
        // Delete related data first (checklists, appointments, etc.)
        for (const pregnancy of existingPregnancies) {
          // Get checklist IDs first
          const { data: checklists } = await supabase
            .from('user_checklists')
            .select('id')
            .eq('pregnancy_id', pregnancy.id);

          if (checklists && checklists.length > 0) {
            const checklistIds = checklists.map(c => c.id);

            // Delete user_checklist_items first
            await supabase
              .from('user_checklist_items')
              .delete()
              .in('checklist_id', checklistIds);

            // Delete user_checklists
            await supabase
              .from('user_checklists')
              .delete()
              .eq('pregnancy_id', pregnancy.id);
          }

          // Delete user_appointments
          await supabase
            .from('user_appointments')
            .delete()
            .eq('pregnancy_id', pregnancy.id);

          // Delete the pregnancy itself
          await supabase
            .from('pregnancies')
            .delete()
            .eq('id', pregnancy.id);
        }
      }

      // Prepare profile update
      const profileUpdate: Record<string, unknown> = {
        id: user.id,
        email: user.email,
        display_name: getUserDisplayName(user),
        role: 'user',
        user_role: data.userRole, // father or partner
        birth_date: data.birthDate || null,
        gender: effectiveGender,
      };

      // Handle premium sharing based on who has premium
      if (inviterPremiumStatus?.hasPremium) {
        // Inviter has premium - share it with partner (but don't make partner a beta tester!)
        profileUpdate.subscription_tier = 'premium';
        profileUpdate.premium_shared_from = partnerConnection.user_1_id;
        profileUpdate.premium_shared_at = new Date().toISOString();

        // If partner has their own paid subscription, schedule it for cancellation
        if (partnerSubscriptionInfo?.hasActiveSubscription && partnerSubscriptionInfo?.stripeSubscriptionId) {
          try {
            // Call Edge Function to cancel subscription at period end
            const { error: cancelError } = await supabase.functions.invoke('cancel-subscription-at-period-end', {
              body: {
                subscriptionId: partnerSubscriptionInfo.stripeSubscriptionId,
                reason: 'partner_premium_sharing'
              }
            });
            if (cancelError) {
              console.error('Error scheduling subscription cancellation:', cancelError);
              // Don't throw - continue with onboarding even if cancellation fails
            }
          } catch (cancelErr) {
            console.error('Exception scheduling subscription cancellation:', cancelErr);
          }
        }
      } else if (validatedPromoCode) {
        // Partner redeemed a promo code - they become the premium owner and share with inviter
        // This will be handled by handleFinalComplete when the promo code is redeemed
        // For now, just mark that premium will be shared TO the inviter
        profileUpdate.subscription_tier = 'premium';
      }

      // Update profile with personal info and beta status
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert(profileUpdate, { onConflict: 'id' });

      if (profileError) {
        console.error('Profile update error:', profileError);
        throw new Error(t('messages.profileUpdateError'));
      }

      // Set the shared pregnancy in the store
      if (partnerConnection.pregnancy) {
        setPregnancy(partnerConnection.pregnancy);
      }

      // Move to completion step (partner skips beta code entry)
      setCurrentStep(totalSteps);
    } catch (err) {
      console.error('Partner onboarding error:', err);
      setError(t('common.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Complete standard onboarding (full flow with pregnancy creation)
  const handleComplete = async () => {
    if (!user) return;
    if (!data.babyAlreadyBorn && !data.expectedDueDate) return;
    if (data.babyAlreadyBorn && !data.actualBirthDate) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const effectiveGender = getEffectiveGender();

      // First, delete any existing pregnancies created by this user
      // This ensures we don't have multiple pregnancies (including completed ones from baby mode)
      const { data: existingPregnancies } = await supabase
        .from('pregnancies')
        .select('id')
        .eq('created_by', user.id);

      if (existingPregnancies && existingPregnancies.length > 0) {
        console.log('Deleting existing pregnancies before creating new one:', existingPregnancies);
        // Delete related data first (checklists, appointments, etc.)
        for (const pregnancy of existingPregnancies) {
          // Get checklist IDs first
          const { data: checklists } = await supabase
            .from('user_checklists')
            .select('id')
            .eq('pregnancy_id', pregnancy.id);

          if (checklists && checklists.length > 0) {
            const checklistIds = checklists.map(c => c.id);

            // Delete user_checklist_items first
            await supabase
              .from('user_checklist_items')
              .delete()
              .in('checklist_id', checklistIds);

            // Delete user_checklists
            await supabase
              .from('user_checklists')
              .delete()
              .eq('pregnancy_id', pregnancy.id);
          }

          // Delete user_appointments
          await supabase
            .from('user_appointments')
            .delete()
            .eq('pregnancy_id', pregnancy.id);

          // Delete name swipes and matches
          await supabase.from('name_swipes').delete().eq('pregnancy_id', pregnancy.id);
          await supabase.from('name_matches').delete().eq('pregnancy_id', pregnancy.id);

          // Delete activity feed entries
          await supabase.from('activity_feed').delete().eq('pregnancy_id', pregnancy.id);

          // Delete pregnancy info
          await supabase.from('pregnancy_info').delete().eq('pregnancy_id', pregnancy.id);

          // Update partner connections to 'pregnancy_deleted'
          await supabase
            .from('partner_connections')
            .update({ status: 'pregnancy_deleted' })
            .eq('pregnancy_id', pregnancy.id)
            .eq('status', 'active');

          // Delete pending partner connections
          await supabase
            .from('partner_connections')
            .delete()
            .eq('pregnancy_id', pregnancy.id)
            .eq('status', 'pending');

          // Delete the pregnancy itself
          await supabase
            .from('pregnancies')
            .delete()
            .eq('id', pregnancy.id);
        }
      }

      // Ensure profile exists and update with personal info
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert(
          {
            id: user.id,
            email: user.email,
            display_name: getUserDisplayName(user),
            role: 'user',
            user_role: data.userRole, // mother, father, or partner
            birth_date: data.birthDate || null,
            gender: effectiveGender,
          },
          {
            onConflict: 'id',
          }
        );

      if (profileError) {
        console.error('Profile creation error:', profileError);
        throw new Error(t('messages.profileCreateError'));
      }

      // Calculate high-risk status
      const isHighRisk = calculateHighRisk();

      // Create pregnancy record - either active or completed (baby already born)
      const birthDateStr = data.actualBirthDate?.toISOString().split('T')[0];
      const dueDateStr = data.babyAlreadyBorn
        ? birthDateStr // Use birth date as due date when baby is already born
        : data.expectedDueDate!.toISOString().split('T')[0];

      const pregnancyInsert: Record<string, unknown> = {
        expected_due_date: dueDateStr,
        child_number: data.childNumber,
        child_count: data.childCount,
        is_high_risk: isHighRisk,
        baby_names: [],
        created_by: user.id,
        is_active: !data.babyAlreadyBorn,
      };

      // If baby is already born, set completion fields
      if (data.babyAlreadyBorn && birthDateStr) {
        pregnancyInsert.completion_type = 'birth';
        pregnancyInsert.completion_date = birthDateStr;
        pregnancyInsert.actual_birth_date = birthDateStr;
      }

      const { data: pregnancy, error: pregnancyError } = await supabase
        .from('pregnancies')
        .insert(pregnancyInsert)
        .select()
        .single();

      if (pregnancyError) throw pregnancyError;

      // Update the pregnancy store so HomePage knows we have a pregnancy
      if (data.babyAlreadyBorn) {
        // For baby mode, set as last completed pregnancy (not active)
        setPregnancy(null);
      } else {
        setPregnancy(pregnancy);
      }

      // If partner invite requested, create pending connection.
      // Läuft bewusst über createPartnerInvite statt über ein eigenes INSERT:
      // ein zweiter Pfad mit eigener Token-Erzeugung und ohne Duplikatsprüfung
      // war die Ursache für mehrfach angelegte Einladungen.
      if (data.invitePartner && data.partnerEmail && pregnancy) {
        // Determine primary user (mother = primary)
        const primaryUserId = data.userRole === 'mother' ? user.id : null;

        const { error: connectionError } = await createPartnerInvite(pregnancy.id, user.id, {
          primaryUserId,
        });

        if (connectionError) {
          console.error('Failed to create partner connection:', connectionError);
          // Don't throw - pregnancy was created successfully
        }

        // TODO: Send invitation email via Supabase Edge Function or external service
      }

      // Move to premium/promo step (step 8) - handles premium skip internally
      setCurrentStep(8);
    } catch (err) {
      console.error('Onboarding error:', err);
      setError(t('common.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFinish = () => {
    navigate('/');
  };

  // Get premium message for CompletionStep
  const getPremiumMessage = (): string => {
    if (!validatedPromoCode) return '';

    if (validatedPromoCode.codeType === 'beta') {
      return t('messages.betaWelcome');
    }

    if (validatedPromoCode.isLifetime) {
      return t('messages.premiumLifetime');
    }

    const months = validatedPromoCode.promoMonths;
    if (months === 1) return t('messages.premiumOneMonth');
    if (months === 12) return t('messages.premiumOneYear');
    return t('messages.premiumMonths', { months });
  };

  // Final completion - redeem promo code if present and navigate
  const handleFinalComplete = async () => {
    if (!user) {
      navigate('/');
      return;
    }

    // If there's a validated promo code, redeem it
    if (validatedPromoCode) {
      try {
        const { data: result, error } = await supabase.rpc('redeem_promo_code', {
          p_user_id: user.id,
          p_code: validatedPromoCode.code,
        });

        if (error) {
          console.error('Error redeeming promo code:', error);
          // Postgres unique_violation (parallel double-click) → Lifetime-Cap
          if ((error as { code?: string }).code === '23505') {
            setError(t('promo.errors.lifetimeCap'));
          } else {
            setError(t('promo.errors.invalidCode'));
          }
        } else if (result && typeof result === 'object' && 'success' in result && !result.success) {
          const errorCode = (result as { error_code?: string }).error_code;
          switch (errorCode) {
            case 'lifetime_cap':
              setError(t('promo.errors.lifetimeCap'));
              break;
            case 'already_premium':
              setError(t('promo.errors.alreadyPremium'));
              break;
            case 'invalid_code':
              setError(t('promo.errors.invalidCode'));
              break;
            default:
              setError(t('promo.errors.invalidCode'));
          }
        } else {
          console.log('Promo code redeemed:', result);
        }
      } catch (err) {
        console.error('Error redeeming promo code:', err);
        setError(t('promo.errors.invalidCode'));
      }
    }

    navigate('/');
  };

  const handleCancel = async () => {
    await signOut();
    navigate('/login');
  };

  // Build subscription cancellation warning message
  const getSubscriptionWarningMessage = (): string | null => {
    if (!inviterPremiumStatus?.hasPremium || !partnerSubscriptionInfo?.hasActiveSubscription) {
      return null;
    }
    const endDate = partnerSubscriptionInfo.subscriptionEndDate
      ? new Date(partnerSubscriptionInfo.subscriptionEndDate).toLocaleDateString(locale === 'en' ? 'en-US' : 'de-DE', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        })
      : t('messages.subscriptionWarningFallback');
    return t('messages.subscriptionWarning', { name: partnerConnection?.inviterName || t('messages.yourPartner'), date: endDate });
  };

  // Get custom completion message for partner
  const getPartnerCompletionMessage = (): string => {
    const baseMes = t('messages.partnerConnected', { name: partnerConnection?.inviterName || t('messages.yourPartner') });
    if (validatedPromoCode) {
      return `${baseMes}\n\n${getPremiumMessage()}`;
    }
    return baseMes;
  };

  // Render steps for invited partner (flow varies based on premium status)
  const renderPartnerStep = () => {
    const inviterHasPremium = inviterPremiumStatus?.hasPremium ?? false;

    // Flow WITH inviter premium: Welcome → PregnancyDetails → Role → PersonalInfo → Completion (5 steps)
    // Flow WITHOUT inviter premium: Welcome → PregnancyDetails → Role → PersonalInfo → Premium → PromoCode → Completion (7 steps)

    if (inviterHasPremium) {
      // Simplified flow - inviter has premium
      switch (currentStep) {
        case 1:
          return (
            <WelcomeStep
              name={displayName}
              onNext={handleNext}
              onCancel={handleCancel}
              customMessage={t('messages.partnerInvite', { name: partnerConnection?.inviterName || t('messages.yourPartner') })}
            />
          );
        case 2:
          if (!partnerConnection?.pregnancy) {
            return (
              <div className="text-center p-4">
                <p className="text-white/70">{t('pregnancyDetails.loading')}</p>
              </div>
            );
          }
          return (
            <PregnancyDetailsStep
              details={{
                expectedDueDate: partnerConnection.pregnancy.expected_due_date,
                childCount: partnerConnection.pregnancy.child_count,
                babyNames: partnerConnection.pregnancy.baby_names || [],
                inviterName: partnerConnection.inviterName,
                completionType: partnerConnection.pregnancy.completion_type,
                actualBirthDate: partnerConnection.pregnancy.actual_birth_date,
                babyData: partnerConnection.pregnancy.baby_data,
              }}
              onNext={handleNext}
              onBack={handleBack}
              warningMessage={getSubscriptionWarningMessage()}
            />
          );
        case 3:
          return (
            <RoleStep
              value={data.userRole}
              onChange={(role) => handleUpdateData({ userRole: role })}
              onNext={handleNext}
              onBack={handleBack}
            />
          );
        case 4:
          return (
            <PersonalInfoStep
              birthDate={data.birthDate}
              gender={data.gender}
              userRole={data.userRole}
              onBirthDateChange={(date) => handleUpdateData({ birthDate: date })}
              onGenderChange={(gender) => handleUpdateData({ gender })}
              onNext={handlePartnerComplete}
              onBack={handleBack}
              isSubmitting={isSubmitting}
              error={error}
              submitLabel={t('common.saveProfile')}
            />
          );
        case 5:
          return (
            <CompletionStep
              onFinish={handleFinish}
              customMessage={getPartnerCompletionMessage()}
              variant="partner"
            />
          );
        default:
          return null;
      }
    } else {
      // Extended flow - inviter doesn't have premium, offer premium upsell
      switch (currentStep) {
        case 1:
          return (
            <WelcomeStep
              name={displayName}
              onNext={handleNext}
              onCancel={handleCancel}
              customMessage={t('messages.partnerInvite', { name: partnerConnection?.inviterName || t('messages.yourPartner') })}
            />
          );
        case 2:
          if (!partnerConnection?.pregnancy) {
            return (
              <div className="text-center p-4">
                <p className="text-white/70">{t('pregnancyDetails.loading')}</p>
              </div>
            );
          }
          return (
            <PregnancyDetailsStep
              details={{
                expectedDueDate: partnerConnection.pregnancy.expected_due_date,
                childCount: partnerConnection.pregnancy.child_count,
                babyNames: partnerConnection.pregnancy.baby_names || [],
                inviterName: partnerConnection.inviterName,
                completionType: partnerConnection.pregnancy.completion_type,
                actualBirthDate: partnerConnection.pregnancy.actual_birth_date,
                babyData: partnerConnection.pregnancy.baby_data,
              }}
              onNext={handleNext}
              onBack={handleBack}
            />
          );
        case 3:
          return (
            <RoleStep
              value={data.userRole}
              onChange={(role) => handleUpdateData({ userRole: role })}
              onNext={handleNext}
              onBack={handleBack}
            />
          );
        case 4:
          return (
            <PersonalInfoStep
              birthDate={data.birthDate}
              gender={data.gender}
              userRole={data.userRole}
              onBirthDateChange={(date) => handleUpdateData({ birthDate: date })}
              onGenderChange={(gender) => handleUpdateData({ gender })}
              onNext={handlePartnerComplete}
              onBack={handleBack}
              isSubmitting={isSubmitting}
              error={error}
              submitLabel={t('common.next')}
            />
          );
        case 5:
          // Show PromoCodeStep if triggered, otherwise PremiumStep
          if (showPromoCodeStep) {
            return (
              <PromoCodeStep
                onNext={(promoCode) => {
                  setValidatedPromoCode(promoCode);
                  setShowPromoCodeStep(false);
                  if (promoCode) {
                    handleNext(); // Go to completion
                  }
                }}
                onBack={() => setShowPromoCodeStep(false)}
              />
            );
          }
          return (
            <PremiumStep
              onNext={handleNext}
              onBack={handleBack}
              onPromoCode={() => setShowPromoCodeStep(true)}
            />
          );
        case 6:
          // PromoCodeStep (when not triggered from PremiumStep overlay)
          if (showPromoCodeStep) {
            return (
              <PromoCodeStep
                onNext={(promoCode) => {
                  setValidatedPromoCode(promoCode);
                  setShowPromoCodeStep(false);
                  handleNext();
                }}
                onBack={() => setShowPromoCodeStep(false)}
              />
            );
          }
          // Skip to completion if no promo step needed
          return (
            <CompletionStep
              onFinish={handleFinalComplete}
              customMessage={getPartnerCompletionMessage()}
              variant="partner"
            />
          );
        case 7:
          return (
            <CompletionStep
              onFinish={handleFinalComplete}
              customMessage={getPartnerCompletionMessage()}
              variant="partner"
            />
          );
        default:
          return null;
      }
    }
  };

  // Render steps for standard onboarding
  const renderStandardStep = () => {
    switch (currentStep) {
      case 1:
        return <WelcomeStep name={displayName} onNext={handleNext} onCancel={handleCancel} />;
      case 2:
        return (
          <RoleStep
            value={data.userRole}
            onChange={(role) => handleUpdateData({ userRole: role })}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 3:
        return (
          <PersonalInfoStep
            birthDate={data.birthDate}
            gender={data.gender}
            userRole={data.userRole}
            onBirthDateChange={(date) => handleUpdateData({ birthDate: date })}
            onGenderChange={(gender) => handleUpdateData({ gender })}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 4:
        return (
          <DueDateStep
            value={data.expectedDueDate}
            onChange={(date) => handleUpdateData({ expectedDueDate: date })}
            onNext={handleNext}
            onBack={handleBack}
            babyAlreadyBorn={data.babyAlreadyBorn}
            onBabyAlreadyBornChange={(born) => handleUpdateData({ babyAlreadyBorn: born })}
            birthDate={data.actualBirthDate}
            onBirthDateChange={(date) => handleUpdateData({ actualBirthDate: date })}
          />
        );
      case 5:
        return (
          <PregnancyTypeStep
            value={data.childCount}
            onChange={(count) => handleUpdateData({ childCount: count })}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 6:
        return (
          <ChildNumberStep
            value={data.childNumber}
            onChange={(num) => handleUpdateData({ childNumber: num })}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 7:
        return (
          <PartnerStep
            invitePartner={data.invitePartner}
            partnerEmail={data.partnerEmail}
            onChange={(updates) => handleUpdateData(updates)}
            onNext={handleComplete}
            onBack={handleBack}
            isSubmitting={isSubmitting}
            error={error}
            existingConnection={existingPartnerConnection}
          />
        );
      case 8:
        // If user already has premium, skip to completion
        if (subscription.isPremium) {
          return (
            <CompletionStep
              onFinish={handleFinalComplete}
              customMessage={t('premium.alreadyPremium')}
            />
          );
        }
        // Show PromoCodeStep as overlay if triggered
        if (showPromoCodeStep) {
          return (
            <PromoCodeStep
              onNext={(promoCode) => {
                setValidatedPromoCode(promoCode);
                setShowPromoCodeStep(false);
                if (promoCode) {
                  // Code validated, go to completion
                  handleNext();
                }
              }}
              onBack={() => setShowPromoCodeStep(false)}
            />
          );
        }
        return (
          <PremiumStep
            onNext={handleNext}
            onBack={handleBack}
            onPromoCode={() => setShowPromoCodeStep(true)}
          />
        );
      case 9:
        return (
          <CompletionStep
            onFinish={handleFinalComplete}
            customMessage={validatedPromoCode ? getPremiumMessage() : undefined}
          />
        );
      default:
        return null;
    }
  };

  // Show loading while checking partner status
  if (isCheckingPartnerStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-white animate-spin mx-auto mb-4" />
          <p className="text-white/70">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // Actual steps shown in progress (excluding completion)
  const progressSteps = totalSteps - 1;

  return (
    <div className="min-h-screen flex flex-col safe-area-top">
      {/* Step Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div>
            {isInvitedPartner ? renderPartnerStep() : renderStandardStep()}
          </div>
        </div>
      </div>

      {/* Step Indicators */}
      {currentStep < totalSteps && (
        <div className="flex justify-center pb-4 safe-area-bottom">
          <div className="flex space-x-2">
            {Array.from({ length: progressSteps }, (_, i) => (
              <div
                key={i}
                className={cn(
                  'w-2 h-2 rounded-full transition-colors',
                  i + 1 <= currentStep ? 'bg-pp-orange' : 'bg-white/20'
                )}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
